import type { Feature, FeatureCollection, GeoJsonObject, GeoJsonProperties, Geometry } from 'geojson'
import * as L from 'leaflet'

import shp from 'shpjs'
import { Logger, NOOP_VOID, Presenter } from 'wdc-cube'
import FilterManager from '../tcm_filter_manager'
import { TheConsumerMarketPresenter } from '../tcm_presenter'
import { MapCardScope, ScopeDefaults } from '../tcm_scopes'
import { type EmpresaLocationBean, type GeoLocation } from '../tcm_service'
import * as utils from './utils'

import { TheConsumerMarketService, type EmpresaFilter, type FetchRequest, type FetchResponse } from '../tcm_service'
import LMapClickSimulation from './lmap_click_simulation'
import BaseSelectionModeBehaviour from './selection_mode_behaviour'
import AreaSelectionModeBehaviour from './selection_mode_behaviour_area'
import CircleSelectionModeBehaviour from './selection_mode_behaviour_circle'
import MapSelectionModeBehaviour from './selection_mode_behaviour_map'
import RouteSelectionModeBehaviour from './selection_mode_behaviour_route'

const LOG = Logger.get('TcmPresenterForMap')

const service = TheConsumerMarketService.singleton()

const MARK_CIRCLE_CLASSES = static_buildMarkCircleClasses()

type MapPointEntry = {
    coordenadas: L.LatLngLiteral
    empresas: EmpresaLocationBean[]
}

type TerritoryGeoJson = FeatureCollection<Geometry, GeoJsonProperties> | Feature<Geometry, GeoJsonProperties> | Geometry

type TerritoryLayer = {
    id: string
    name: string
    data: TerritoryGeoJson | null
    defaultVisible: boolean
    __leafletLayer?: L.GeoJSON<TerritoryLayer, Geometry>
    __selectedLayers: L.Path[]
}

let ENCHENTE_RS_2024_FEATURE: GeoJsonObject | null = null
const TERRITORY_LAYERS: TerritoryLayer[] = []

export class TcmPresenterForMap extends Presenter<MapCardScope, TheConsumerMarketPresenter> {
    // Constructor

    constructor(owner: TheConsumerMarketPresenter) {
        super(owner, owner.scope.mapa)
        this.__parent = owner
        this.__filterManager = owner.filterManager

        this.__areaSelectionBehaviour = new AreaSelectionModeBehaviour()
        this.__areaSelectionBehaviour.owner = this

        this.__routeSelectionBehaviour = new RouteSelectionModeBehaviour()
        this.__routeSelectionBehaviour.owner = this

        this.__circleSelectionBehaviour = new CircleSelectionModeBehaviour()
        this.__circleSelectionBehaviour.owner = this

        this.__mapSelectionBehaviour = new MapSelectionModeBehaviour()
        this.__mapSelectionBehaviour.owner = this

        this.__selecionBehaviour = this.__mapSelectionBehaviour
        this.__preloadTerritoryLayers()

        owner.scope.filterPane.enchenteRs2024.onNotifyMap = this.action(this.__enchenteRs2024Changed)
    }

    // Instance

    private readonly __parent: TheConsumerMarketPresenter
    private readonly __filterManager: FilterManager
    private __map: L.Map | null = null
    private __mapCenter = ScopeDefaults.getDefaultCenter()
    private __layerGroup: L.LayerGroup<unknown> | null = null
    private __loaded = false
    private __loadingTerritories = false
    private __fetching = false
    private __dataChangedDuringFetch = false
    private __fetchRequestCount = 0
    private __requestHash = ''
    private __boundHash = ''
    private __dataset: EmpresaLocationBean[] = []
    private __clickSimulation = new LMapClickSimulation()
    private __removeEnchenteRs2024Layer = utils.staticNoopOther
    private __removeMapLayer = utils.staticNoopOther
    private __cancelTerritoryResync = utils.staticNoopOther

    private readonly __areaSelectionBehaviour: AreaSelectionModeBehaviour
    private readonly __routeSelectionBehaviour: RouteSelectionModeBehaviour
    private readonly __circleSelectionBehaviour: CircleSelectionModeBehaviour
    private readonly __mapSelectionBehaviour: MapSelectionModeBehaviour
    private __selecionBehaviour: BaseSelectionModeBehaviour

    get filterManager() {
        return this.__filterManager
    }

    get keyboard() {
        return this.__parent.scope.keyboard
    }

    async initialize() {
        this.__clickSimulation.clickListener = this.__handleMapClickEvent.bind(this)

        this.scope.update = this.update
        this.scope.onMapChanged = this.action(this.__handleMapChanged)
        this.scope.onClearFilters = this.action(this.__handleClearFilters)
        this.scope.onSetAreaMode = this.action(this.__handleSetAreaMode)
        this.scope.onSetRouteMode = this.action(this.__handleSetRouteMode)
        this.scope.onSetCircleMode = this.action(this.__handleSetCircleMode)
        this.scope.onSetMapMode = this.action(this.__handleSetMapMode)
        this.scope.onExpandClick = this.action(this.__handleExpandClick)
        this.scope.onShrunkClick = this.action(this.__handleShrunkClick)

        this.scope.slider.update = this.update
        this.scope.slider.onValueChanged = this.action(this.__handleSliderValueChanged)

        this.__selecionBehaviour.setMode(15)
    }

    override release(): void {
        this.__clickSimulation.unbind()
        this.__cancelTerritoryResync()
        this.__removeEnchenteRs2024Layer()
        this.__removeMapLayer()
        super.release()
    }

    /**
     * Dispara sempre que um this.update() for invocado
     * Roda uma única vez por ciclo de atualizacao.
     *
     * Ideal para calcular valores derivados
     */
    override onBeforeScopeUpdate() {
        this.scope.showClearButton = this.hasFilter()
        this.__selecionBehaviour.onBeforeScopeUpdate()
    }

    isLoaded() {
        return this.__loaded
    }

    clear() {
        this.__boundHash = ''
        this.__requestHash = ''
        this.__areaSelectionBehaviour.clear()
        this.__routeSelectionBehaviour.clear()
        this.__circleSelectionBehaviour.clear()
        this.__mapSelectionBehaviour.clear()
        this.update()
    }

    hasFilter() {
        return (
            this.__areaSelectionBehaviour.hasFilter() ||
            this.__routeSelectionBehaviour.hasFilter() ||
            this.__circleSelectionBehaviour.hasFilter() ||
            this.__mapSelectionBehaviour.hasFilter()
        )
    }

    getBounds(): GeoLocation[] {
        if (this.__map) {
            const bounds = this.__map.getBounds()
            return utils.twoLocationToReactablePolygon(
                {
                    lon: bounds.getWest(),
                    lat: bounds.getNorth()
                },
                {
                    lon: bounds.getEast(),
                    lat: bounds.getSouth()
                }
            )
        }
        return this.getBrasilBounds()
    }

    getBrasilBounds(): GeoLocation[] {
        return [
            {
                lat: -39.57182223734373,
                lon: -74.88281250000001
            },
            {
                lat: 11.092165893502013,
                lon: -74.88281250000001
            },
            {
                lat: 11.092165893502013,
                lon: -20.654296875000004
            },
            {
                lat: -39.57182223734373,
                lon: -20.654296875000004
            }
        ]
    }

    private async __handleExpandClick() {
        this.scope.expanded = true
        this.__parent.scope.maximizeCard = 'mapa'
    }

    private async __handleShrunkClick() {
        this.scope.expanded = false
        this.__parent.scope.maximizeCard = ''
    }

    private async __handleClearFilters() {
        if (this.keyboard.ctrlKey) {
            this.__boundHash = ''
            this.__requestHash = ''
            this.__selecionBehaviour.clear()
        } else if (this.__selecionBehaviour.hasFilter()) {
            this.clear()
        }
        this.update()
        this.__renderElements(true)
    }

    private async __handleSetAreaMode() {
        this.__removeMapLayer()
        const zoomValue = this.__map ? this.__map.getZoom() : 4
        this.__selecionBehaviour = this.__areaSelectionBehaviour
        this.__selecionBehaviour.setMode(zoomValue)
        this.__selecionBehaviour.onBeforeScopeUpdate()
        this.update()
    }

    private async __handleSetRouteMode() {
        this.__removeMapLayer()
        const zoomValue = this.__map ? this.__map.getZoom() : 4
        this.__selecionBehaviour = this.__routeSelectionBehaviour
        this.__selecionBehaviour.setMode(zoomValue)
        this.__selecionBehaviour.onBeforeScopeUpdate()
        this.update()
    }

    private async __handleSetCircleMode() {
        this.__removeMapLayer()
        const zoomValue = this.__map ? this.__map.getZoom() : 4
        this.__selecionBehaviour = this.__circleSelectionBehaviour
        this.__selecionBehaviour.setMode(zoomValue)
        this.__selecionBehaviour.onBeforeScopeUpdate()
        this.update()
    }

    private async __handleSetMapMode() {
        this.__preloadTerritoryLayers()

        const zoomValue = this.__map ? this.__map.getZoom() : 4
        this.__selecionBehaviour = this.__mapSelectionBehaviour
        this.__selecionBehaviour.setMode(zoomValue)
        this.__selecionBehaviour.onBeforeScopeUpdate()
        this.update()
    }

    private async __handleSliderValueChanged(value: number) {
        this.scope.slider.value = value
        if (this.__fetching) {
            this.__dataChangedDuringFetch = true
            this.__fetchRequestCount++
        } else {
            const mgr = this.__filterManager
            this.__selecionBehaviour.applyFilter(mgr)
            if (this.__selecionBehaviour.hasFilter()) {
                await this.__renderElements(true)
            }
        }
    }

    private async __handleMapChanged(map: L.Map | null) {
        const changed = this.__map !== map
        if (this.__map && changed) {
            this.__cancelTerritoryResync()
            this.__removeEnchenteRs2024Layer()
            this.__removeMapLayer()
            this.__clickSimulation.unbind()
            this.__map.off('moveend')
            this.__map.off('zoomend')
            this.__selecionBehaviour.removeAllLayers()
        }

        this.__map = map
        if (map && changed) {
            this.__clickSimulation.bind(map)

            this.__selecionBehaviour.setZoom(map.getZoom())

            map.on('moveend', this.__onMapMoveEndEvent.bind(this))
            map.on('zoomend', this.__handleZoomEndEvent.bind(this))

            if (!this.__mapCenter) {
                this.__mapCenter = ScopeDefaults.getDefaultCenter()
            }
            if (this.__layerGroup) {
                this.__layerGroup.addTo(map)
                map.setView(this.__mapCenter, 4)
            } else {
                map.setView(this.__mapCenter, 4)
            }

            if (this.__dataset.length > 0) {
                this.plotPlaces()
            }

            this.__preloadTerritoryLayers()
            this.__enchenteRs2024Changed()
            this.__areaSelectionBehaviour.draw(map)
            this.__routeSelectionBehaviour.draw(map)
            this.__circleSelectionBehaviour.draw(map)
            this.__mapSelectionBehaviour.draw(map)
        }
    }

    async __enchenteRs2024Changed() {
        const map = this.__map
        if (!map) return

        this.__removeEnchenteRs2024Layer()

        const enchenteRs2024Active = this.owner.scope.filterPane.enchenteRs2024.active
        if (enchenteRs2024Active) {
            try {
                if (!ENCHENTE_RS_2024_FEATURE) {
                    const geoResp = await fetch('data/inundacao_em_6-8_de_maio_de_2024.json', {
                        method: 'GET'
                    })

                    ENCHENTE_RS_2024_FEATURE = await geoResp.json()
                }

                if (ENCHENTE_RS_2024_FEATURE) {
                    const layer = L.geoJSON(ENCHENTE_RS_2024_FEATURE, {
                        style: {
                            color: utils.REGION_COLOR
                        }
                    }).addTo(map)
                    this.__removeEnchenteRs2024Layer = () => {
                        layer.removeFrom(map)
                        this.__removeEnchenteRs2024Layer = utils.staticNoopOther
                    }
                }
            } catch (e) {
                LOG.error('Reading GEOJSON', e)
            }
        }
    }

    plotPlaces() {
        this.scope.showClearButton = this.__selecionBehaviour.hasFilter()
        const leafletMap = this.__map
        if (!leafletMap) {
            return
        }
        const dataset = this.__dataset

        if (this.__layerGroup) {
            this.__layerGroup.remove()
            this.__layerGroup = null
        }
        const layerGroup = L.layerGroup()

        let center = ScopeDefaults.getDefaultCenter()
        const southWest: L.LatLngLiteral = { ...center }
        const northEast: L.LatLngLiteral = { ...center }

        const entryMap = new Map<string, MapPointEntry>()

        for (const row of dataset) {
            if (!row.endereco_location) continue

            const key = `${row.endereco_location.lat}:${row.endereco_location.lon}`
            let entry = entryMap.get(key)
            if (!entry) {
                entry = {
                    coordenadas: {
                        lat: row.endereco_location.lat,
                        lng: row.endereco_location.lon
                    },
                    empresas: []
                }
                entryMap.set(key, entry)
            }
            entry.empresas.push(row)
        }

        if (entryMap.size > 0) {
            const entryValuesIt = entryMap.values()[Symbol.iterator]()
            let entry = entryValuesIt.next()
            if (!entry.done) {
                const coordenadas = entry.value.coordenadas!
                northEast.lng = southWest.lng = coordenadas.lng
                northEast.lat = southWest.lat = coordenadas.lat

                center.lat = coordenadas.lat
                center.lng = coordenadas.lng
            }

            while (!entry.done) {
                const { lng, lat } = entry.value.coordenadas!

                southWest.lng = Math.min(southWest.lng, lng)
                northEast.lng = Math.max(northEast.lng, lng)

                northEast.lat = Math.min(northEast.lat, lat)
                southWest.lat = Math.max(southWest.lat, lat)

                const names = entry.value.empresas.map((e) => `${e.cnpj} - ${e.nome}`)

                let markClassName = MARK_CIRCLE_CLASSES[0]
                if (entry.value.empresas.length) {
                    markClassName = MARK_CIRCLE_CLASSES[1]
                } else if (entry.value.empresas.length > 0) {
                    markClassName = MARK_CIRCLE_CLASSES[2]
                }

                const marker = new L.Marker(
                    { lng, lat },
                    {
                        title: names.join('\n'),
                        icon: L.divIcon({ className: markClassName, iconSize: L.point(12, 12) })
                    }
                )
                layerGroup.addLayer(marker)

                entry = entryValuesIt.next()
            }
        }

        layerGroup.addTo(leafletMap)
        if (this.__boundHash === '') {
            if (entryMap.size === 1) {
                leafletMap.setView(center, 15, { animate: false })
            } else if (entryMap.size > 1) {
                const bounds = new L.LatLngBounds(southWest, northEast)
                center = bounds.getCenter()
                leafletMap.fitBounds(bounds, { maxZoom: 15, animate: false })
            } else if (!this.__selecionBehaviour.hasFilter()) {
                leafletMap.setView(center, 4, { animate: false })
            }
        }

        this.__layerGroup = layerGroup
        this.__mapCenter = center
        this.__loaded = true
    }

    async parseTerritoryByPath(filePath: string, displayName: string, visible = false) {
        const response = await fetch(filePath)
        if (!response.ok) {
            throw new Error(`Nao foi possivel carregar: ${filePath}`)
        }

        if (filePath.toLowerCase().endsWith('.zip')) {
            const buffer = await response.arrayBuffer()
            const geo = await shp(buffer)
            const normalized = normalizeGeoJson(geo, displayName, visible)
                .map((layer) => ({ ...layer, data: sanitizeGeoJson(layer.data) }))
                .filter((layer) => layer.data)
            return normalized
        }

        const json = await response.json()
        const normalized = normalizeGeoJson(json, displayName, visible)
            .map((layer) => ({ ...layer, data: sanitizeGeoJson(layer.data) }))
            .filter((layer) => layer.data)
        return normalized
    }

    async __preloadTerritoryLayers() {
        const leafletMap = this.__map
        if (!leafletMap) {
            return
        }

        if (this.__loadingTerritories) {
            return
        }

        try {
            this.__loadingTerritories = true
            if (!TERRITORY_LAYERS.length) {
                let layers: Array<{ file?: string; name?: string; visible?: boolean }> = []

                const manifestResponse = await fetch('data/territorios/manifest.json')
                if (!manifestResponse.ok) {
                    throw new Error(`Nao encontrei data/territorios/manifest.json.`)
                }
                const manifest = await manifestResponse.json()
                layers = Array.isArray(manifest.layers) ? manifest.layers : []

                if (!layers.length) {
                    LOG.error('Nenhuma camada territorial encontrada no manifesto.')
                    return
                }

                LOG.debug('Pre-carregando camadas territoriais...')
                for (const layer of layers) {
                    const fileName = String(layer.file || '').trim()
                    if (!fileName || !layer.visible) {
                        continue
                    }

                    const encodedFile = encodeURIComponent(fileName).replace(/%2F/g, '/')
                    const filePath = `data/territorios/${encodedFile}`
                    const displayName = String(layer.name || fileName)
                    const visible = Boolean(layer.visible)
                    const parsed = await this.parseTerritoryByPath(filePath, displayName, visible)
                    TERRITORY_LAYERS.push(...parsed)
                }
            }

            this.__removeMapLayer()

            TERRITORY_LAYERS.forEach((layer) => {
                if (!layer.data) return

                const addLayer = L.geoJSON(layer.data, this.__mapSelectionBehaviour.mapGeoJsonOptions()).addTo(
                    leafletMap
                )

                layer.__leafletLayer = addLayer
                layer.__selectedLayers = []

                this.__removeMapLayer = () => {
                    addLayer.removeFrom(leafletMap)
                    this.__removeMapLayer = utils.staticNoopOther
                }
            })

            this.__scheduleTerritoryResync(leafletMap)

            LOG.debug(`Camadas territoriais pre-carregadas: ${TERRITORY_LAYERS.length} camada(s).`)
        } catch (error) {
            LOG.error(`${error}`)
        } finally {
            this.__loadingTerritories = false
        }
    }

    private __scheduleTerritoryResync(map: L.Map) {
        this.__cancelTerritoryResync()

        const resync = () => {
            if (this.__map === map) {
                map.fire('viewreset')
            }
        }

        L.Util.requestAnimFrame(resync)
        map.once('zoomend', resync)
        map.once('moveend', resync)

        this.__cancelTerritoryResync = () => {
            map.off('zoomend', resync)
            map.off('moveend', resync)
            this.__cancelTerritoryResync = utils.staticNoopOther
        }
    }

    private __handleMapClickEvent(evt: L.LeafletMouseEvent) {
        this.__selecionBehaviour.addLocation({
            lat: evt.latlng.lat,
            lon: evt.latlng.lng
        })
        this.__renderElements(true).catch(LOG.caught)
    }

    private __handleZoomEndEvent() {
        if (this.__map) {
            this.__selecionBehaviour.setZoom(this.__map.getZoom())
        }
    }

    private async __onMapMoveEndEvent() {
        if (this.__selecionBehaviour.hasFilter()) {
            return
        }

        const bounds = this.getBounds()

        const newRequestHash = utils.buildHash(bounds)
        if (this.__boundHash !== newRequestHash) {
            this.__boundHash = newRequestHash
            if (!this.__selecionBehaviour.hasFilter()) {
                if (this.__fetching) {
                    this.__fetchRequestCount++
                } else {
                    const filter = this.__filterManager.build()
                    await this.fetch(filter, true)
                }
            }
        }
    }

    private __prepareFetch(request: FetchRequest, baseZoom?: boolean) {
        baseZoom = baseZoom || this.hasFilter()
        const requestId = this.__fetchRequestCount

        let newRequestHash = this.__requestHash
        let newBoundHash = this.__boundHash

        let viewPortBounds: GeoLocation[] | undefined = undefined
        let bounds = this.getBounds()
        if (!this.hasFilter()) {
            viewPortBounds = bounds
        }
        if (!baseZoom) {
            bounds = this.getBrasilBounds()
            viewPortBounds = bounds
        }

        newRequestHash = utils.buildHash({ request, listagem_localizacao: viewPortBounds })
        newBoundHash = utils.buildHash(bounds)

        if (newRequestHash !== this.__requestHash) {
            request.listagem_localizacao = viewPortBounds ? { viewPortBounds } : true

            return (resp: FetchResponse) => {
                this.__handleResponse(resp, requestId, newRequestHash, newBoundHash, baseZoom)
            }
        }

        return NOOP_VOID
    }

    async fetch(filter: EmpresaFilter, baseZoom?: boolean) {
        try {
            this.__dataChangedDuringFetch = false
            this.__fetching = true

            const request: FetchRequest = { filter }
            const postAction = this.__prepareFetch(request, baseZoom)
            if (request.listagem_localizacao) {
                const resp = await service.fetch(request)
                postAction(resp)
            }
        } finally {
            this.__fetching = false
        }
    }

    private __handleResponse(
        resp: FetchResponse,
        requestId: number,
        newRequestHash: string,
        newBoundHash: string,
        baseZoom?: boolean
    ) {
        this.__dataset = resp.listagem_localizacao ?? []
        this.__requestHash = newRequestHash
        this.__boundHash = newBoundHash

        if (!baseZoom) {
            this.zoomAdjust()
        }

        this.plotPlaces()

        if (requestId !== this.__fetchRequestCount) {
            this.__renderElements(this.__dataChangedDuringFetch).catch(LOG.caught)
        }
    }

    private zoomAdjust() {
        const leafletMap = this.__map
        if (!leafletMap) {
            return
        }

        const dataset = this.__dataset

        const center = ScopeDefaults.getDefaultCenter()

        const southWest: L.LatLngLiteral = dataset.length > 0 ? ({} as L.LatLngLiteral) : { ...center }
        const northEast: L.LatLngLiteral = dataset.length > 0 ? ({} as L.LatLngLiteral) : { ...center }

        if (this.hasFilter() || this.__filterManager.hasFilter()) {
            for (const row of dataset) {
                if (!row.endereco_location) continue

                const { lon, lat } = row.endereco_location

                southWest.lng = !southWest.lng ? lon : Math.min(southWest.lng, lon)
                northEast.lng = !northEast.lng ? lon : Math.max(northEast.lng, lon)

                northEast.lat = !northEast.lat ? lat : Math.min(northEast.lat, lat)
                southWest.lat = !southWest.lat ? lat : Math.max(southWest.lat, lat)
            }
        } else {
            southWest.lng = -73.4415929
            northEast.lng = -32.3988874

            northEast.lat = -33.7395789
            southWest.lat = 4.9408084
        }

        const bounds = new L.LatLngBounds(southWest, northEast)

        leafletMap.flyToBounds(bounds, { maxZoom: 15 })
    }

    private async __renderElements(fetchData: boolean) {
        this.__boundHash = ''
        this.update()
        this.__selecionBehaviour.preparePoints()
        if (this.__map) {
            this.__selecionBehaviour.draw(this.__map)
        }

        const mgr = this.__filterManager

        this.__areaSelectionBehaviour.applyFilter(mgr)
        this.__routeSelectionBehaviour.applyFilter(mgr)
        this.__circleSelectionBehaviour.applyFilter(mgr)
        this.__mapSelectionBehaviour.applyFilter(mgr)

        if (fetchData) {
            this.__dataChangedDuringFetch = false
            await this.__parent.fetchData('')
        }
    }

    plotFromFilterManager() {
        if (this.__map) {
            const mgr = this.__filterManager
            this.__areaSelectionBehaviour.plotFromFilter(this.__map, mgr)
            this.__routeSelectionBehaviour.plotFromFilter(this.__map, mgr)
            this.__circleSelectionBehaviour.plotFromFilter(this.__map, mgr)
            this.__mapSelectionBehaviour.plotFromFilter(this.__map, mgr)
        }
    }

    getMapLayerByLocation(location: GeoLocation): L.Polygon | L.Polyline | undefined {
        for (const territory of TERRITORY_LAYERS) {
            const geoLayer = territory.__leafletLayer
            if (!geoLayer) continue

            const layerInLocation = geoLayer
                .getLayers()
                .find(
                    (layer) =>
                        (layer instanceof L.Polygon || layer instanceof L.Polyline) &&
                        layer.getBounds &&
                        layer.getBounds().contains({ lat: location.lat, lng: location.lon })
                )

            if (layerInLocation) {
                return layerInLocation as L.Polygon | L.Polyline
            }
        }
        return undefined
    }

    getMapLayerByUF(uf: string): L.Polygon | L.Polyline | undefined {
        if (!uf) {
            return undefined
        }

        for (const territory of TERRITORY_LAYERS) {
            const geoLayer = territory.__leafletLayer
            if (!geoLayer) continue

            const layerInLocation = geoLayer
                .getLayers()
                .find(
                    (layer) =>
                        (layer instanceof L.Polygon || layer instanceof L.Polyline) &&
                        layer.feature &&
                        layer.feature.properties['SIGLA_UF'] === uf
                )

            if (layerInLocation) {
                return layerInLocation as L.Polygon | L.Polyline
            }
        }
        return undefined
    }
}

function static_buildMarkCircleClasses() {
    return [
        'gg-ue-red-circle',
        'gg-islamic-green-circle',
        'gg-duke-blue-circle',
        'gg-mughal-green-circle',
        'gg-weldon-blue-circle'
    ]
}

function normalizeGeoJson(
    input: shp.FeatureCollectionWithFilename | shp.FeatureCollectionWithFilename[],
    name: string,
    defaultVisible = false
): TerritoryLayer[] {
    if (Array.isArray(input)) {
        return input.map((geo, idx) => ({
            id: `${name}-${idx}`,
            name: `${name} (${idx + 1})`,
            data: geo as TerritoryGeoJson,
            defaultVisible,
            __selectedLayers: []
        }))
    }

    return [
        {
            id: name,
            name,
            data: input as TerritoryGeoJson,
            defaultVisible,
            __selectedLayers: []
        }
    ]
}

function isFiniteCoord(value: unknown) {
    return typeof value === 'number' && Number.isFinite(value)
}

function hasValidCoordinates(coords: unknown): boolean {
    if (!Array.isArray(coords)) {
        return false
    }

    if (coords.length === 2 && isFiniteCoord(coords[0]) && isFiniteCoord(coords[1])) {
        return true
    }

    return coords.some((item) => hasValidCoordinates(item))
}

function hasValidGeometry(geometry: Geometry): boolean {
    if (geometry.type === 'GeometryCollection') {
        return geometry.geometries.some(hasValidGeometry)
    }

    return hasValidCoordinates((geometry as Exclude<Geometry, { type: 'GeometryCollection' }>).coordinates)
}

function sanitizeGeoJson(geo: TerritoryGeoJson | null): TerritoryGeoJson | null {
    if (!geo) return null

    if (isFeatureCollection(geo)) {
        const features = geo.features.filter((feature) => feature.geometry && hasValidGeometry(feature.geometry))

        return {
            ...geo,
            features
        }
    }

    if (isFeature(geo)) {
        if (!geo.geometry || !hasValidGeometry(geo.geometry)) {
            return null
        }

        return geo
    }

    if (isGeometry(geo)) {
        if (!hasValidGeometry(geo)) {
            return null
        }
    }

    return geo
}

function isFeatureCollection(geo: TerritoryGeoJson): geo is FeatureCollection<Geometry, GeoJsonProperties> {
    return geo.type === 'FeatureCollection'
}

function isFeature(geo: TerritoryGeoJson): geo is Feature<Geometry, GeoJsonProperties> {
    return geo.type === 'Feature'
}

function isGeometry(geo: TerritoryGeoJson): geo is Geometry {
    return geo.type !== 'Feature' && geo.type !== 'FeatureCollection'
}
